<template>
  <div>
    <b-container class="pt-5 pb-5">
      <div class="text-center">
        <img
          alt="Tixgo"
          src="../../assets/logo_white.svg"
          class="mb-5"
          width="120"
        />
      </div>

      <template v-if="step == 1">
        <ValidationObserver ref="form1" v-slot="{ invalid, validate }" slim>
          <b-form
            @submit.prevent="validate().then(nextStep)"
            autocomplete="off"
          >
            <b-card class="small-card mb-3">
              <h2 class="fs-4 font-weight-bold">Jouw team account</h2>
              <p class="text-muted mb-4">
                Maak hieronder een account aan waarmee je jouw locatie kunt
                beheren in Tixgo.
              </p>

              <b-row>
                <b-col md="6">
                  <ValidationProvider rules="required" v-slot="{ errors }">
                    <div class="mb-3">
                      <label class="required">Voornaam</label>
                      <b-form-input v-model="form.firstname" />
                      <div v-if="errors.length" class="invalid-feedback">
                        {{ errors[0] }}
                      </div>
                    </div>
                  </ValidationProvider>
                </b-col>

                <b-col md="6">
                  <ValidationProvider rules="required" v-slot="{ errors }">
                    <div class="mb-3">
                      <label class="required">Achternaam</label>
                      <b-form-input v-model="form.lastname" />
                      <div v-if="errors.length" class="invalid-feedback">
                        {{ errors[0] }}
                      </div>
                    </div>
                  </ValidationProvider>
                </b-col>
              </b-row>

              <ValidationProvider
                rules="required|email|unique"
                v-slot="{ errors }"
              >
                <div class="mb-3">
                  <label class="required">E-mailadres</label>
                  <b-form-input @change="verifyEmail" v-model="form.username" />
                  <div v-if="errors.length" class="invalid-feedback">
                    {{ errors[0] }}
                  </div>
                </div>
              </ValidationProvider>

              <template v-if="exists">
                <b-alert variant="info" show>
                  Dit team account bestaat reeds.
                  <router-link to="/account/login"
                    >Login om uw account te beheren</router-link
                  >.
                </b-alert>
              </template>

              <ValidationProvider rules="required" v-slot="{ errors }">
                <div class="mb-3">
                  <label class="required">Wachtwoord</label>
                  <b-form-input v-model="form.password" type="password" />
                  <div v-if="errors.length" class="invalid-feedback">
                    {{ errors[0] }}
                  </div>
                </div>
              </ValidationProvider>

              <div class="clearfix">
                <b-button
                  type="submit"
                  :disabled="invalid"
                  variant="primary"
                  class="float-right"
                >
                  Volgende
                  <font-awesome-icon
                    class="ml-1"
                    icon="fa-solid fa-chevron-right"
                  />
                </b-button>
              </div>
            </b-card>
          </b-form>
        </ValidationObserver>
      </template>

      <template v-if="step == 2">
        <ValidationObserver ref="form2" v-slot="{ invalid, validate }" slim>
          <b-form
            @submit.prevent="validate().then(saveStep)"
            autocomplete="off"
          >
            <b-card class="small-card mb-3">
              <h2 class="fs-4 font-weight-bold">Je evenement locatie</h2>
              <p class="text-muted mb-4">
                Vul hieronder de gegevens van je locatie in. Deze informatie
                wordt gebruikt om jouw locatie weer te geven op de
                evenementenpagina.
              </p>
              <ValidationProvider rules="required" v-slot="{ errors }">
                <div class="mb-3">
                  <label class="required">Naam van locatie</label>
                  <b-form-input v-model="form.name" />
                  <div v-if="errors.length" class="invalid-feedback">
                    {{ errors[0] }}
                  </div>
                </div>
              </ValidationProvider>

              <ValidationProvider rules="required" v-slot="{ errors }">
                <div class="mb-3">
                  <label class="required">Adres van locatie</label>
                  <b-form-input v-model="form.place" />
                  <div v-if="errors.length" class="invalid-feedback">
                    {{ errors[0] }}
                  </div>
                </div>
              </ValidationProvider>

              <ValidationProvider rules="required" v-slot="{ errors }">
                <div class="mb-3">
                  <label class="required">Omschrijving van locatie</label>
                  <VueTrix v-model="form.description" />
                  <div v-if="errors.length" class="invalid-feedback">
                    {{ errors[0] }}
                  </div>
                </div>
              </ValidationProvider>

              <ValidationProvider rules="email" v-slot="{ errors }">
                <div class="mb-3">
                  <label>E-mailadres</label>
                  <p class="text-muted mb-2">
                    Wordt weergegeven op de detail pagina van de locatie.
                  </p>
                  <b-form-input v-model="form.email" />
                  <div v-if="errors.length" class="invalid-feedback">
                    {{ errors[0] }}
                  </div>
                </div>
              </ValidationProvider>

              <ValidationProvider rules="required" v-slot="{ errors }">
                <div class="mb-3">
                  <label class="required">Foto van locatie</label>
                  <p class="text-muted">
                    Optimaal formaat: 1920x1080 / 4:3 ratio (max. 5MB)
                  </p>
                  <DropFile
                    v-model="form.header_image"
                    accept="image/png,image/jpeg"
                    :instant="false"
                  />
                  <p class="text-muted font-italic">
                    De organisator behoudt zich het recht voor om de foto’s van
                    de locatie te wijzigen of te verwijderen indien deze niet
                    voldoen aan de kwaliteits- of andere geldende richtlijnen.
                  </p>
                  <div v-if="errors.length" class="invalid-feedback">
                    {{ errors[0] }}
                  </div>
                </div>
              </ValidationProvider>

              <div class="clearfix">
                <b-button @click="prevStep" variant="outline-primary">
                  <font-awesome-icon
                    class="mr-1"
                    icon="fa-solid fa-chevron-left"
                  />
                  Vorige
                </b-button>
                <b-button
                  type="submit"
                  :disabled="invalid || isSend"
                  variant="primary"
                  class="float-right"
                >
                  Toevoegen
                  <font-awesome-icon
                    class="ml-1"
                    icon="fa-solid fa-chevron-right"
                  />
                </b-button>
              </div>
            </b-card>
          </b-form>
        </ValidationObserver>
      </template>

      <template v-if="step == 3"> Bedankt! </template>
    </b-container>
  </div>
</template>

<script>
import { extend, ValidationObserver, ValidationProvider } from "vee-validate";
import VueTrix from "vue-trix";
import DropFile from "@/components/DropFile";

export default {
  metaInfo: {
    title: "Locatie aanmelden",
  },
  components: {
    DropFile,
    VueTrix,
    ValidationObserver,
    ValidationProvider,
  },
  data() {
    return {
      exists: false,
      step: 1,
      isSend: false,
      form: {
        shop: "7d982ae0",
        firstname: "",
        lastname: "",
        username: "",
        password: "",
        name: "",
        place: "",
        description: "",
        email: "",
        header_image: null,
      },
    };
  },
  computed: {},
  methods: {
    nextStep: function () {
      this.step++;
    },
    saveStep: function () {
      this.isSend = true;

      const formData = new FormData();

      Object.keys(this.form).forEach((key) => {
        const value = this.form[key];
        if (value === null || value === "") return;
        formData.append(key, value);
      });

      this.$axios
        .post("https://api.tixgo.nl/teams/storeinvite", formData)
        .then((response) => {
          if (response.data.status == "success") {
            this.$axios
              .post("https://api.tixgo.nl/accounts/login", this.form)
              .then((response) => {
                if (response.data.status == "success") {
                  localStorage.setItem("type", response.data.session.type);
                  localStorage.setItem("token", response.data.session.token);
                  localStorage.setItem("role", response.data.session.role);

                  this.$axios.defaults.headers.common = {
                    Authorization: `Bearer ${localStorage.getItem("token")}`,
                  };

                  this.$router.push("/");
                }
              });
          } else {
            this.error = "Er is iets fout gegaan. Probeer het opnieuw.";
            this.isSend = false;
          }
        });
    },
    verifyEmail: function () {
      this.$axios
        .post("https://api.tixgo.nl/teams/verifyemail", this.form)
        .then((response) => {
          if (response.data.status == "error") {
            this.exists = true;
          } else {
            this.exists = false;
          }
        });
    },
    prevStep: function () {
      this.step--;
    },
    async saveLocation() {
      let check = await this.$refs["form"].validate();

      if (!check) {
        window.scrollTo({
          top: 0,
          behavior: "smooth",
        });

        return;
      }

      this.$axios
        .post("https://api.tixgo.nl/teams/saveinvite", this.form)
        .then((response) => {
          response;
          this.$router.push("/manage/organisation/locations");
        });
    },
  },
  created() {
    const isUnique = (value) =>
      new Promise((resolve) => {
        this.$axios
          .post("https://api.tixgo.nl/teams/verifyemail", this.form)
          .then((response) => {
            if (response.data.status == "error") {
              return resolve({
                valid: false,
                data: {
                  message: `${value} is already taken.`,
                },
              });
            } else {
              return resolve({
                valid: true,
              });
            }
          });
      });

    extend("unique", {
      validate: isUnique,
      message: "Dit e-mailadres is reeds in gebruik.",
      getMessage: (field, params, data) => data.message,
    });
  },
};
</script>
<style scoped>
.small-card {
  margin: auto;
}

@media screen and (min-width: 576px) {
  .small-card {
    max-width: 900px;
  }
}
</style>
